import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FileUp, Loader2, Sparkles } from 'lucide-react';
import { AlertBanner, Button, Modal, StatusBadge } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/axios';
import { type AnalyseAOResult, analyserAoPdf } from '@/services/ia';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';

const CONFIANCE: Record<string, StatusKey> = { HAUTE: 'done', MOYENNE: 'working', FAIBLE: 'stuck' };

export function AnalyseIAModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AnalyseAOResult | null>(null);

  const mutation = useMutation({
    mutationFn: (f: File) => analyserAoPdf(f),
    onSuccess: (r) => {
      setResult(r);
      toast.success('Analyse terminée — vérifiez les données.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const ext = result?.extraction;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Analyser un appel d'offres avec l'IA"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button onClick={() => file && mutation.mutate(file)} disabled={!file || mutation.isPending}>
            {mutation.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} Analyser
          </Button>
        </>
      }
    >
      <AlertBanner variant="warning" className="mb-4">
        L'IA assiste, l'humain valide. Les quantités du DQE doivent être vérifiées manuellement avant tout chiffrage.
        La clé API reste côté serveur.
      </AlertBanner>

      <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-surface-primary p-6 text-center transition-colors hover:bg-surface-hover">
        <FileUp className="size-7 text-brand-primary" />
        <span className="text-sm font-medium text-content-primary">{file ? file.name : 'Téléverser le PDF de l\'appel d\'offres'}</span>
        <span className="text-xs text-content-muted">PDF, max 20 Mo</span>
        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); }} />
      </label>

      {mutation.isPending && (
        <div className="mt-6 flex items-center justify-center gap-3 text-sm text-content-secondary">
          <Loader2 className="size-5 animate-spin text-brand-primary" /> Analyse du document en cours…
        </div>
      )}

      {ext && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-content-primary">Données extraites</h3>
            <StatusBadge status={CONFIANCE[ext.confiance_extraction] ?? 'pending'} label={`Confiance : ${ext.confiance_extraction}`} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {([
              ['Intitulé', ext.intitule],
              ['Maître d\'ouvrage', ext.maitre_ouvrage],
              ['Type de marché', ext.type_marche],
              ['Wilaya', ext.wilaya],
              ['Date limite', ext.date_limite_depot ?? '—'],
              ['Montant estimatif', ext.montant_estimatif != null ? formatDZD(ext.montant_estimatif) : '—'],
              ['Caution soumission', ext.caution_soumission != null ? formatDZD(ext.caution_soumission) : '—'],
              ['Délai (mois)', ext.delai_execution_mois != null ? String(ext.delai_execution_mois) : '—'],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="rounded-md border border-border bg-surface-secondary px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-status-working">À vérifier · {k}</p>
                <p className="text-sm font-medium text-content-primary">{v}</p>
              </div>
            ))}
          </div>

          {ext.dqe?.length > 0 && (
            <div>
              <p className="mb-2 label-caps">DQE extrait ({ext.dqe.length} lignes)</p>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface-primary">
                    <tr className="text-left">
                      <th className="px-3 py-2 label-caps">Désignation</th>
                      <th className="px-3 py-2 label-caps">Unité</th>
                      <th className="px-3 py-2 text-right label-caps">Quantité</th>
                      <th className="px-3 py-2 text-right label-caps">P.U. estimé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ext.dqe.map((d, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-2">{d.designation}</td>
                        <td className="px-3 py-2">{d.unite}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{d.quantite}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{d.prix_unitaire_estime != null ? formatDZD(d.prix_unitaire_estime, { decimals: 0 }) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ext.remarques && <AlertBanner variant="info">{ext.remarques}</AlertBanner>}
        </div>
      )}
    </Modal>
  );
}
